<template>
  <manual-page
    title="Gestión de Órdenes"
    description="Guía completa para procesar, preparar, enviar y entregar las órdenes de tu sucursal."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'ordersIndex' }"
    action-text="Ir a Órdenes"
  >
    <!-- Overview -->
    <h2>Vista General de Órdenes</h2>
    <p>
      Las órdenes son el corazón de la operación de tu sucursal. Cada orden representa
      un pedido de alimento que debe prepararse y entregarse a un cliente.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">La pantalla de órdenes muestra:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Cliente:</strong> Nombre del cliente que realizó el pedido</li>
        <li><strong>Mascota:</strong> Para quién es el alimento</li>
        <li><strong>Fecha de entrega:</strong> Cuándo debe entregarse</li>
        <li><strong>Tipo de entrega:</strong> Envío a domicilio o recolección</li>
        <li><strong>Estado:</strong> En qué punto del proceso está</li>
        <li><strong>Monto:</strong> Total de la orden</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Haz clic en cualquier orden para ver su detalle completo,
        incluyendo la composición de la dieta, dirección de entrega y notas especiales.
      </p>
    </div>

    <!-- Order States -->
    <h2>Estados de las Órdenes</h2>
    <p>
      Cada orden pasa por diferentes estados durante su ciclo de vida.
      Es importante entender cada estado para gestionar correctamente las órdenes.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pendiente de Pago</h3>
          <p class="text-gray-600">
            La orden fue generada automáticamente según el plan del cliente.
            El sistema intentará cobrar en la fecha programada. No requiere
            acción de tu parte hasta que se complete el pago.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pagada</h3>
          <p class="text-gray-600">
            El cobro fue exitoso. La orden está lista para entrar a producción.
            Revisa la fecha de entrega para priorizar las órdenes más urgentes.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">En Preparación</h3>
          <p class="text-gray-600">
            El alimento está siendo elaborado. Este estado indica que
            estás trabajando activamente en la orden.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Lista para Envío / Recolección</h3>
          <p class="text-gray-600">
            El alimento está preparado y empacado. Dependiendo del tipo de entrega:
          </p>
          <ul class="list-disc list-inside text-gray-600 mt-2 space-y-1">
            <li><strong>Envío:</strong> Debe programarse la entrega a domicilio</li>
            <li><strong>Recolección:</strong> El cliente vendrá a recogerla</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Entregada</h3>
          <p class="text-gray-600">
            Orden completada exitosamente. El cliente recibió su alimento
            ya sea por entrega a domicilio o recolección en sucursal.
          </p>
        </div>
      </div>
    </div>

    <!-- Failed Orders -->
    <h2>Órdenes Fallidas</h2>
    <p>
      Las órdenes pueden fallar por problemas de pago o de entrega.
      Es importante gestionar estos casos correctamente.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Fallo de Pago</h3>
          <p class="text-gray-600">
            Cuando el cobro falla repetidamente, la orden pasa a estado "Fallida".
            Causas comunes: tarjeta vencida, fondos insuficientes, tarjeta bloqueada.
            El equipo central contactará al cliente para resolver el problema de pago.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Fallo de Envío</h3>
          <p class="text-gray-600">
            Si una entrega no puede completarse (dirección incorrecta, cliente ausente,
            problema con el transportista), puedes marcarla como "Fallida".
            Esto permite reagendar el envío para otro día.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Cuando una orden falla en el envío y se regresa a
        "Lista para envío", se puede volver a intentar la entrega. El sistema generará
        un nuevo cargo de envío automáticamente al momento de entregar.
      </p>
    </div>

    <!-- Filtering -->
    <h2>Filtrar Órdenes</h2>
    <p>
      Usa los filtros para organizar y encontrar órdenes específicas:
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Filtros disponibles:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Por estado:</strong> Pendientes, Pagadas, En preparación, Listas, Entregadas, Fallidas</li>
        <li><strong>Por fecha:</strong> Rango de fechas de entrega</li>
        <li><strong>Por tipo:</strong> Envío a domicilio o recolección en sucursal</li>
        <li><strong>Búsqueda:</strong> Por nombre de cliente o mascota</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Para tu operación diaria, filtra por "Pagadas" para ver
        las órdenes que necesitan entrar a producción, luego por "Listas" para coordinar entregas.
      </p>
    </div>

    <!-- Processing Orders -->
    <h2>Procesar Órdenes</h2>
    <p>
      El flujo típico para procesar una orden desde que está pagada hasta entregarla:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Revisar Órdenes Pagadas</h3>
          <p class="text-gray-600">
            Consulta las órdenes pagadas y revisa las fechas de entrega
            para priorizar cuáles preparar primero.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Iniciar Preparación</h3>
          <p class="text-gray-600">
            Selecciona las órdenes a preparar y haz clic en "Iniciar preparación".
            Puedes iniciar múltiples órdenes a la vez si vas a prepararlas en lote.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Elaborar el Alimento</h3>
          <p class="text-gray-600">
            Prepara el alimento según la receta de la dieta. En el detalle de la orden
            puedes ver la composición exacta: ingredientes, cantidades y notas especiales.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Marcar como Lista</h3>
          <p class="text-gray-600">
            Una vez empacado el alimento, marca la orden como "Lista para envío"
            o "Lista para recolección" según corresponda.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Coordinar Entrega</h3>
          <p class="text-gray-600">
            Para envíos a domicilio, programa la entrega con el servicio de paquetería.
            Para recolecciones, notifica al cliente que su pedido está listo.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">6</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Confirmar Entrega</h3>
          <p class="text-gray-600">
            Cuando el cliente reciba su pedido, marca la orden como "Entregada".
            El sistema registra la fecha y hora de finalización.
          </p>
        </div>
      </div>
    </div>

    <!-- Shipping Integration -->
    <h2>Envíos a Domicilio</h2>
    <p>
      El sistema está integrado con servicios de paquetería para facilitar
      los envíos a domicilio.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Generar Guía de Envío</h3>
          <p class="text-gray-600">
            Desde el detalle de una orden lista para envío, puedes generar
            la guía de paquetería. El sistema calcula el costo según la zona.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Imprimir Etiqueta</h3>
          <p class="text-gray-600">
            Imprime la etiqueta de envío y pégala en el paquete.
            La etiqueta incluye código de barras para rastreo.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Seguimiento</h3>
          <p class="text-gray-600">
            El sistema actualiza automáticamente el estado cuando el
            paquete se entrega. También puedes rastrear el envío manualmente.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Agrupa los envíos del día para optimizar la recolección
        del transportista y reducir costos operativos.
      </p>
    </div>

    <!-- Pickup Orders -->
    <h2>Recolección en Sucursal</h2>
    <p>
      Algunos clientes prefieren recoger su pedido directamente en la sucursal.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Notificar al Cliente</h3>
          <p class="text-gray-600">
            Cuando la orden está lista, el cliente recibe una notificación
            automática en la app indicando que puede pasar a recoger.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Verificar Identidad</h3>
          <p class="text-gray-600">
            Al entregar, verifica que sea el cliente correcto.
            Puedes usar el nombre o el número de orden como referencia.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Confirmar Entrega</h3>
          <p class="text-gray-600">
            Marca la orden como "Entregada" una vez que el cliente
            reciba su pedido y lo verifique.
          </p>
        </div>
      </div>
    </div>

    <!-- Order Detail -->
    <h2>Detalle de la Orden</h2>
    <p>
      Al abrir una orden, verás toda la información necesaria para procesarla:
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Información disponible:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Datos del cliente:</strong> Nombre, teléfono, email</li>
        <li><strong>Datos de la mascota:</strong> Nombre, especie, peso</li>
        <li><strong>Composición de la dieta:</strong> Ingredientes y cantidades exactas</li>
        <li><strong>Dirección de entrega:</strong> Con indicaciones especiales</li>
        <li><strong>Notas:</strong> Instrucciones especiales del cliente</li>
        <li><strong>Historial:</strong> Cambios de estado con fechas y horas</li>
      </ul>
    </div>

    <!-- Advance Requests -->
    <h2>Solicitudes de Adelanto</h2>
    <p>
      Los clientes pueden solicitar recibir su orden antes de la fecha programada.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Recibir Solicitud</h3>
          <p class="text-gray-600">
            Recibirás una notificación cuando un cliente solicite adelantar
            su entrega. La solicitud incluye la nueva fecha deseada.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Evaluar Capacidad</h3>
          <p class="text-gray-600">
            Revisa si tienes capacidad para producir y entregar en la
            nueva fecha solicitada. Considera tu carga de trabajo actual.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Aprobar o Rechazar</h3>
          <p class="text-gray-600">
            Aprueba la solicitud si puedes cumplir, o recházala si no es posible.
            Al aprobar, la fecha de entrega se actualiza automáticamente.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Responde las solicitudes de adelanto lo antes posible
        para que el cliente pueda hacer otros planes si es necesario.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Prácticas</h2>
    <ul>
      <li><strong>Revisa las órdenes diariamente:</strong> Consulta las órdenes pagadas cada mañana</li>
      <li><strong>Prioriza por fecha:</strong> Procesa primero las órdenes con entrega más cercana</li>
      <li><strong>Actualiza estados en tiempo real:</strong> Mantén el sistema actualizado para seguimiento preciso</li>
      <li><strong>Lee las notas:</strong> Revisa siempre las instrucciones especiales antes de preparar</li>
      <li><strong>Verifica el empaque:</strong> Asegúrate de que el alimento esté bien empacado antes de marcar como listo</li>
      <li><strong>Confirma entregas inmediatamente:</strong> Marca como entregada apenas el cliente reciba su pedido</li>
    </ul>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes problemas con las órdenes:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualOrders",
  components: { ManualPage },
  data() {
    return {
      relatedLinks: [
        { route: "manualProductionSchedule", label: "Programa de Producción" },
        { route: "manualClients", label: "Clientes" },
        { route: "manualInventory", label: "Inventario" },
        { route: "manualGettingStarted", label: "Primeros Pasos" },
      ],
    };
  },
};
</script>
